import { MapPin, ExternalLink, Compass, Target } from "lucide-react";
import { SITE } from "@/lib/site";
import PageSeo from "@/components/PageSeo";

export default function About() {
  return (
    <>
    <PageSeo
      title="About The Barracks Bistro — Wellington, Nilgiris"
      description="Set beside Wellington Lake within the Madras Regimental Centre, The Barracks Bistro is inspired by military heritage, warm hospitality and hill-country flavours."
      path="/about"
    />
    <div data-testid="about-page" className="pt-32 pb-24">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="eyebrow">Our Story</div>
        <h1 className="mt-3 text-5xl sm:text-6xl max-w-3xl leading-[1]">
          Heritage, hospitality, <br />
          <span className="italic text-[hsl(var(--brass))]">culinary excellence.</span>
        </h1>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-14">
          <div className="space-y-5 text-[hsl(var(--ink-soft))] leading-relaxed" data-testid="about-story">
            <p>
              The Barracks Bistro was established with a vision to create a premium dining destination
              that blends heritage, hospitality, and culinary excellence.
            </p>
            <p>
              Situated in one of the most picturesque locations in Wellington, our restaurant is
              inspired by the proud traditions of the Indian Armed Forces while embracing modern
              dining experiences.
            </p>
            <p>
              From handcrafted coffee to chef-curated meals, every dish reflects our passion for
              quality.
            </p>
            <p>
              As part of <span className="text-[hsl(var(--ink))] font-semibold">Explora ETrades Private Limited (EEPL)</span>,
              The Barracks Bistro represents our commitment to exceptional hospitality and customer
              experiences.
            </p>
          </div>

          <div className="relative">
            <div className="aspect-[4/5] w-full overflow-hidden">
              <img
                src="/images/location.jpg"
                alt="View of The Barracks Bistro and the MRC lake"
                className="w-full h-full object-cover"
                data-testid="about-location-image"
              />
            </div>
            <div className="mt-4 flex items-start justify-between gap-6">
              <div>
                <div className="eyebrow">Location</div>
                <div className="mt-1 text-lg">{SITE.address}</div>
              </div>
              <a
                href={SITE.maps}
                target="_blank"
                rel="noreferrer"
                data-testid="about-maps-link"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--forest))] hover:text-[hsl(var(--brass-2))] whitespace-nowrap"
              >
                <MapPin size={14} /> Open in Maps <ExternalLink size={12} />
              </a>
            </div>
          </div>
        </div>

        {/* Mission + Vision */}
        <div className="mt-24 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="about-mission-vision">
          <article className="bg-[hsl(var(--bg-2))] border border-[hsl(var(--rule))] p-8 md:p-10" data-testid="about-mission">
            <div className="flex items-center gap-3">
              <Target size={22} className="text-[hsl(var(--brass))]" />
              <div className="eyebrow">Our Mission</div>
            </div>
            <div className="rule-brass my-5 max-w-[100px]" />
            <p className="text-lg leading-relaxed text-[hsl(var(--ink))]">
              To deliver <span className="italic text-[hsl(var(--brass-2))]">exceptional food</span>,
              warm hospitality, and memorable experiences while honouring the heritage of our
              surroundings.
            </p>
          </article>

          <article className="bg-[hsl(var(--forest))] text-[hsl(var(--bg))] p-8 md:p-10 relative overflow-hidden" data-testid="about-vision">
            <div className="flex items-center gap-3">
              <Compass size={22} className="text-[hsl(var(--brass))]" />
              <div className="eyebrow !text-[hsl(var(--brass))]">Our Vision</div>
            </div>
            <div className="rule-brass my-5 max-w-[100px]" />
            <p className="text-lg leading-relaxed">
              To become <span className="italic text-[hsl(var(--brass))]">one of South India's most loved</span>{" "}
              destination restaurants, known for culinary excellence and memorable experiences.
            </p>
          </article>
        </div>

        {/* Small credits strip */}
        <div className="mt-16 pt-8 border-t border-[hsl(var(--rule))] flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-[hsl(var(--ink-soft))]">
          <div>A venture of <span className="uppercase tracking-widest text-[hsl(var(--ink))] font-semibold">Explora ETrades Private Limited</span> · EEPL</div>
          <div className="flex items-center gap-4">
            <a href={`mailto:${SITE.email}`} className="hover:text-[hsl(var(--forest))]">{SITE.email}</a>
            <span>·</span>
            <a href={`tel:${SITE.phonePrimary.replace(/\s/g, "")}`} className="hover:text-[hsl(var(--forest))]">{SITE.phonePrimary}</a>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
